/**
 * MAILBOX-UNIT account manager — data layer (2026-09-19 operatör isteği).
 *
 * The 11 mailbox-unit providers (rambler, aol, web.de, libero, tiscali.it,
 * tiscali.cz, vip.sina.com, sina.com, gazeta.pl, rediffmail, t-online.de) are
 * NOT a parent/alias family: each mailbox is ONE sellable unit and the scarce
 * resource is the per-(mailbox, site) sale right. This module is the fetch layer
 * for the shared <MailboxUnitManager> card that all 11 pages adopt.
 *
 * ONE read model feeds the table: GET /admin/providers/:id/site-usage already
 * returns units{}, probe{}, sites[] and mailboxes[]; the manager only needs the
 * mailboxes[] rows (now carrying accountId, aliasId and a derived leaseState).
 *
 * Every mutation nests under the existing /admin/providers/:id/parents Prefix
 * (D-225-safe — no new ingress leaf):
 *   POST   .../parents/:accountId/credentials/reveal   (manage) -> {email,password}
 *   POST   .../parents/:accountId/credentials          (manage) re-encrypt password
 *   POST   .../parents/:accountId/reprobe              (manage) probe-loop nudge
 *   POST   .../parents/:accountId/reset-quarantine     (manage) login_failed->active
 *   POST   .../parents/:accountId/sale-status          (manage) disable/enable
 *   DELETE .../parents/:accountId                      (manage soft / drain hard)
 *   POST   .../parents/export-csv                      (inspect) non-secret CSV
 *   POST   .../parents/bulk-reprobe                    (manage)
 *   POST   .../parents/bulk-take-off-sale              (manage)
 *
 * SECRET HYGIENE: the password is NEVER in the list, CSV, log or URL — only the
 * explicit, audited reveal endpoint returns it (in the response body). This
 * module never echoes a submitted password back into any value it returns.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from '../../api/client.js';

/** The 11 mailbox-unit providers (mail.com/gmx are the SEPARATE alias family). */
export type MailboxUnitProviderId =
  | 'rambler'
  | 'aol'
  | 'webde'
  | 'libero'
  | 'tiscaliit'
  | 'tiscalicz'
  | 'sinavip'
  | 'sina'
  | 'gazetapl'
  | 'rediffmail'
  | 'tonline';

/** Lease state of the single unit alias behind a mailbox. */
export type MailboxLeaseState = 'available' | 'reserved' | 'leased';

/** A tone token compatible with the UI Badge component (kept UI-free here). */
export type MailboxTone = 'success' | 'warn' | 'danger' | 'neutral' | 'info';

/**
 * Raw mailbox row exactly as GET /site-usage returns it (post-extension). Every
 * field is optional so an older payload (before accountId/aliasId/leaseState
 * were added) still normalizes without throwing.
 */
export interface RawMailboxRow {
  readonly address: string;
  readonly accountId?: string;
  readonly aliasId?: string;
  readonly unitStatus?: string;
  readonly accountStatus?: string;
  readonly sessionState?: string;
  readonly leaseState?: MailboxLeaseState;
  readonly verified?: boolean;
  readonly hasCredential?: boolean;
  readonly consecutiveErrors?: number;
  readonly cooldownUntil?: string | null;
  readonly lastSeenAt?: string | null;
  readonly burnedSites?: readonly string[];
  readonly servedSites?: readonly string[];
}

/** The parts of the /site-usage read model the manager consumes. */
export interface MailboxUnitReadModel {
  readonly providerId: string;
  readonly mailboxes?: readonly RawMailboxRow[];
}

/** Normalized row the manager renders and acts on (every field present). */
export interface MailboxRow {
  readonly address: string;
  readonly accountId: string;
  readonly aliasId: string;
  /** Account status: active | login_failed | suspended | disabled. */
  readonly status: string;
  readonly sessionState: string;
  readonly leaseState: MailboxLeaseState;
  readonly consecutiveErrors: number;
  readonly cooldownUntil: string | null;
  readonly lastSeenAt: string | null;
  readonly verified: boolean;
  readonly hasCredential: boolean;
  readonly burnedSites: readonly string[];
  readonly servedSites: readonly string[];
}

function providerBase(providerId: string): string {
  return `/admin/providers/${encodeURIComponent(providerId)}`;
}

function parentBase(providerId: string, accountId: string): string {
  return `${providerBase(providerId)}/parents/${encodeURIComponent(accountId)}`;
}

/** Derive the lease badge from the extended field, falling back to unitStatus. */
function deriveLeaseState(raw: RawMailboxRow): MailboxLeaseState {
  if (raw.leaseState === 'leased' || raw.leaseState === 'reserved' || raw.leaseState === 'available') {
    return raw.leaseState;
  }
  if (raw.unitStatus === 'leased') return 'leased';
  if (raw.unitStatus === 'reserved') return 'reserved';
  return 'available';
}

/** Normalize a raw /site-usage mailbox row into the manager's model. */
export function toMailboxRow(raw: RawMailboxRow): MailboxRow {
  return {
    address: raw.address,
    accountId: raw.accountId ?? '',
    aliasId: raw.aliasId ?? '',
    status: raw.accountStatus ?? 'unknown',
    sessionState: raw.sessionState ?? 'unknown',
    leaseState: deriveLeaseState(raw),
    consecutiveErrors: raw.consecutiveErrors ?? 0,
    cooldownUntil: raw.cooldownUntil ?? null,
    lastSeenAt: raw.lastSeenAt ?? null,
    verified: raw.verified ?? false,
    hasCredential: raw.hasCredential ?? false,
    burnedSites: raw.burnedSites ?? [],
    servedSites: raw.servedSites ?? [],
  };
}

export function toMailboxRows(rows: readonly RawMailboxRow[] | undefined): MailboxRow[] {
  return (rows ?? []).map(toMailboxRow);
}

// ---------------------------------------------------------------------------
// Read model
// ---------------------------------------------------------------------------

export async function fetchMailboxUnits(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
): Promise<ApiResult<MailboxUnitReadModel>> {
  return callAdminApi<MailboxUnitReadModel>(options, { path: `${providerBase(providerId)}/site-usage` });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface RevealResult {
  readonly email: string;
  readonly password: string;
}

/** Explicit, audited password reveal. Returns the secret ONLY in the body. */
export async function revealPassword(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
): Promise<ApiResult<RevealResult>> {
  return liftErrorCode(
    await callAdminApi<RevealResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/credentials/reveal`,
    }),
  );
}

export interface UpdatePasswordInput {
  readonly password: string;
  /** AOL: `web=<web password>;phone=<phone>` (mirrors parseAolCredentialLine). */
  readonly extraSecret?: string;
}

export interface UpdatePasswordResult {
  readonly accountId: string;
  readonly accountHandle: string;
  readonly hasCredential: boolean;
}

export async function updatePassword(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: UpdatePasswordInput,
): Promise<ApiResult<UpdatePasswordResult>> {
  const body: Record<string, unknown> = { password: input.password };
  if (input.extraSecret !== undefined) body.extraSecret = input.extraSecret;
  return liftErrorCode(
    await callAdminApi<UpdatePasswordResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/credentials`,
      body,
    }),
  );
}

export interface ReprobeResult {
  readonly eligibleNextTick: boolean;
  readonly sessionState: string;
}

export async function reprobe(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
): Promise<ApiResult<ReprobeResult>> {
  return liftErrorCode(
    await callAdminApi<ReprobeResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/reprobe`,
    }),
  );
}

export interface ResetQuarantineResult {
  readonly accountId: string;
  readonly outcome: string;
}

export async function resetQuarantine(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  reason: string,
): Promise<ApiResult<ResetQuarantineResult>> {
  return liftErrorCode(
    await callAdminApi<ResetQuarantineResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/reset-quarantine`,
      body: { reason },
    }),
  );
}

export interface SaleStatusInput {
  readonly onSale: boolean;
  readonly reason: string;
}

export interface SaleStatusResult {
  readonly accountId: string;
  readonly onSale: boolean;
}

export async function setSaleStatus(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: SaleStatusInput,
): Promise<ApiResult<SaleStatusResult>> {
  return liftErrorCode(
    await callAdminApi<SaleStatusResult>(options, {
      method: 'POST',
      path: `${parentBase(providerId, accountId)}/sale-status`,
      body: { onSale: input.onSale, reason: input.reason },
    }),
  );
}

export type DeleteMode = 'soft' | 'hard';

export interface DeleteInput {
  readonly mode?: DeleteMode;
  readonly reason: string;
  /** Hard delete only: must equal the accountId (server also enforces). */
  readonly confirmationToken?: string;
}

export interface DeleteResult {
  readonly accountId: string;
  readonly outcome: string;
}

export async function deleteMailbox(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountId: string,
  input: DeleteInput,
): Promise<ApiResult<DeleteResult>> {
  const body: Record<string, unknown> = { reason: input.reason };
  if (input.mode !== undefined) body.mode = input.mode;
  if (input.confirmationToken !== undefined) body.confirmationToken = input.confirmationToken;
  return liftErrorCode(
    await callAdminApi<DeleteResult>(options, {
      method: 'DELETE',
      path: parentBase(providerId, accountId),
      body,
    }),
  );
}

export interface ExportCsvResult {
  readonly csvBase64: string;
  readonly rowCount: number;
}

/** Opt-in CSV of NON-SECRET columns only. Secrets are never exportable. */
export async function exportCsv(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds?: readonly string[],
): Promise<ApiResult<ExportCsvResult>> {
  const body: Record<string, unknown> = {};
  if (accountIds && accountIds.length > 0) body.accountIds = accountIds;
  return liftErrorCode(
    await callAdminApi<ExportCsvResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/export-csv`,
      body,
    }),
  );
}

export interface BulkReprobeResult {
  readonly requested: number;
  readonly nudged: number;
  readonly skippedInFlight: number;
}

export async function bulkReprobe(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds: readonly string[],
): Promise<ApiResult<BulkReprobeResult>> {
  return liftErrorCode(
    await callAdminApi<BulkReprobeResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/bulk-reprobe`,
      body: { accountIds },
    }),
  );
}

export interface BulkTakeOffSaleResult {
  readonly requested: number;
  readonly disabled: number;
  readonly skippedInFlight: number;
}

export async function bulkTakeOffSale(
  options: ApiClientOptions,
  providerId: MailboxUnitProviderId,
  accountIds: readonly string[],
  reason: string,
): Promise<ApiResult<BulkTakeOffSaleResult>> {
  return liftErrorCode(
    await callAdminApi<BulkTakeOffSaleResult>(options, {
      method: 'POST',
      path: `${providerBase(providerId)}/parents/bulk-take-off-sale`,
      body: { accountIds, reason },
    }),
  );
}

// ---------------------------------------------------------------------------
// Helpers (pure — unit-tested)
// ---------------------------------------------------------------------------

/**
 * The shared client lifts `body.reason` into `reason`; error bodies that carry
 * only `{ error }` (e.g. 503 credential_vault_not_configured, 409
 * mailbox_in_flight) would otherwise surface as the opaque `http_<status>`.
 * Lift the server's `error` code so the UI can map it to Turkish text.
 */
export function liftErrorCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !res.reason.startsWith('http_')) return res;
  const code = res.body?.error;
  return typeof code === 'string' && code.length > 0 ? { ...res, reason: code } : res;
}

/**
 * Client-side pristine hint for the hard-delete affordance. The SERVER is
 * authoritative (it also checks reservations + inbox); this only decides whether
 * to SHOW the "Kalıcı sil" tab: never sold, never burned, currently available.
 */
export function isPristineRow(row: MailboxRow): boolean {
  return row.leaseState === 'available' && row.servedSites.length === 0 && row.burnedSites.length === 0;
}

export function leaseStateLabel(state: MailboxLeaseState): { readonly text: string; readonly tone: MailboxTone } {
  if (state === 'leased') return { text: 'Kirada', tone: 'info' };
  if (state === 'reserved') return { text: 'Rezerve', tone: 'warn' };
  return { text: 'Müsait', tone: 'success' };
}

export function statusLabel(status: string): { readonly text: string; readonly tone: MailboxTone } {
  switch (status) {
    case 'active':
      return { text: 'Aktif', tone: 'success' };
    case 'login_failed':
      return { text: 'Giriş başarısız', tone: 'danger' };
    case 'suspended':
      return { text: 'Askıda', tone: 'warn' };
    case 'disabled':
      return { text: 'Devre dışı', tone: 'neutral' };
    default:
      return { text: status, tone: 'neutral' };
  }
}

const REASON_TEXT: Readonly<Record<string, string>> = {
  provider_not_supported: 'Bu sağlayıcı için hesap yönetimi desteklenmiyor.',
  no_credential_on_account: 'Bu hesapta kayıtlı şifre yok.',
  credential_vault_not_configured:
    'Sunucuda kimlik kasası yapılandırılmamış — şifre saklanamadığı için işlem yapılamadı.',
  mailbox_in_flight:
    'Bu mailbox şu anda bir siparişte (rezerve/kirada) — canlı sipariş kapanmadan işlem yapılamaz.',
  mailbox_has_history_use_soft_delete:
    'Bu mailbox daha önce satılmış/kullanılmış — kalıcı silinemez, yumuşak sil kullanın.',
  not_quarantined: 'Bu hesap karantinada değil — çıkarılacak bir şey yok.',
  reprobe_in_flight: 'Doğrulanmış mailbox canlı bir teslimatta — yeniden denenmedi.',
  reason_too_short: 'Gerekçe en az 4 karakter olmalı.',
  invalid_reason: 'Gerekçe en az 4 karakter olmalı.',
  confirmation_required: 'Onay kodu (hesap kimliği) eşleşmedi.',
  invalid_confirmation: 'Onay kodu (hesap kimliği) eşleşmedi.',
  too_many_ids: 'Çok fazla mailbox seçildi — en fazla 500 satır işlenebilir.',
  secrets_not_exportable: 'Gizli sütunlar CSV’ye aktarılamaz.',
  include_secrets_forbidden: 'Gizli sütunlar CSV’ye aktarılamaz.',
  forbidden: 'Yetki yok: bu işlem için gerekli izin hesabınızda yok.',
  auth_required: 'Oturum gerekli — lütfen admin girişi yapın.',
  empty_response_body: 'Sunucu boş yanıt döndü.',
};

/**
 * Turkish operator text for an API / validation reason code. Only the code is
 * ever shown — never a submitted value (a password could be in it).
 */
export function reasonText(reason: string): string {
  const known = REASON_TEXT[reason];
  if (known) return known;
  if (reason.startsWith('network:')) return 'Bağlantı hatası — sunucuya ulaşılamadı.';
  return `İşlem başarısız (${reason}).`;
}

/** Trigger a browser download of a base64 CSV (no secret ever passes here). */
export function downloadCsv(csvBase64: string, filename: string): void {
  const bytes = atob(csvBase64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  const blob = new Blob([buf], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
